import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, ShieldOff, UserCog, Inbox, AlertTriangle, Download, Wrench, UserPlus } from 'lucide-react';
import { Section } from '../FormFields';
import { formatManilaTime } from '../../../utils/formatManilaTime';
import { assignRegionOwner } from '../../../utils/regionOwner';
import { colorForLabel } from '../../../utils/colorHash';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RANGE_OPTIONS = [7, 30, 90];
const RANGE_STORAGE_KEY = 'region-health-range-days';

const Flag = ({ on, label }) => (
  <span className={`inline-flex items-center gap-1 text-xs ${on ? 'text-emerald-600' : 'text-slate-400'}`}>
    {on ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {label}
  </span>
);

const Sparkline = ({ data, days, testId }) => {
  if (!data || data.length === 0 || data.every((v) => v === 0)) return null;
  const w = 64;
  const h = 18;
  const max = Math.max(...data, 1);
  const step = w / (data.length - 1 || 1);
  const points = data.map((v, i) => `${i * step},${(h - 2) - (v / max) * (h - 4)}`).join(' ');
  return (
    <svg width={w} height={h} className="flex-shrink-0" data-testid={testId} title={`Inbound leads, last ${days} days`}>
      <polyline points={points} fill="none" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const RegionHealthPanel = ({ role = 'admin', onFixRegionOwner, refreshSignal }) => {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [assigningKey, setAssigningKey] = useState(null);
  const [rangeDays, setRangeDays] = useState(() => {
    const stored = Number(localStorage.getItem(RANGE_STORAGE_KEY));
    return RANGE_OPTIONS.includes(stored) ? stored : 7;
  });

  const fetchHealth = (days = rangeDays) => {
    axios.get(`${BACKEND_URL}/api/admin/region-settings/health`, { params: { days }, withCredentials: true })
      .then((res) => setRegions(res.data.regions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchHealth(rangeDays);
    const interval = setInterval(() => fetchHealth(rangeDays), 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDays, refreshSignal]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/region-settings/health/export`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `region-health-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // silent — export is a convenience action, region cards remain the source of truth
    } finally {
      setExporting(false);
    }
  };

  const handleMakeOwner = async (candidate, region) => {
    const key = `${candidate.username}-${region}`;
    setAssigningKey(key);
    try {
      await assignRegionOwner(candidate, region);
      toast.success(`${candidate.name} is now the owner of this region`);
      fetchHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign owner');
    } finally {
      setAssigningKey(null);
    }
  };

  if (loading) return <p className="text-sm text-slate-400 italic">Loading region health…</p>;
  if (regions.length === 0) return null;

  return (
    <Section
      title="Region Health"
      description="At-a-glance status for every region — owner, auto-assignment rules, and today's Safe Test Mode suppressions."
    >
      <div className="flex justify-end items-center gap-2 -mt-2">
        <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="region-health-range-selector">
          {RANGE_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => { setRangeDays(d); localStorage.setItem(RANGE_STORAGE_KEY, String(d)); }}
              data-testid={`region-health-range-${d}`}
              className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${rangeDays === d ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
            >
              {d}d
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          data-testid="region-health-export-button"
          className="inline-flex items-center gap-1.5 bg-white border-2 border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:border-slate-400 disabled:opacity-50 transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="region-health-panel">
        {regions.map((r) => {
          const noOwnerAlert = !!r.auto_assign && !r.owner_username;
          return (
            <div
              key={r.region}
              className={`rounded-xl p-3.5 space-y-2 border ${noOwnerAlert ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200'}`}
              data-testid={`region-health-card-${r.region}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm text-slate-800 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorForLabel(r.region) }} data-testid={`region-health-badge-${r.region}`} />
                  {r.name}
                </span>
                {r.suppressed_today > 0 && (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2 py-0.5"
                    data-testid={`region-health-suppressed-${r.region}`}
                  >
                    <ShieldOff className="w-3 h-3" /> {r.suppressed_today} suppressed
                  </span>
                )}
              </div>
              {noOwnerAlert && (
                <div
                  className="space-y-1.5 text-xs font-medium text-red-700 bg-red-100 border border-red-200 rounded-lg px-2 py-1.5"
                  data-testid={`region-health-alert-${r.region}`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <span className="flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> Auto-assign is ON but this region has no owner
                    </span>
                    {role === 'admin' && onFixRegionOwner && (
                      <button
                        type="button"
                        onClick={() => onFixRegionOwner(r.region)}
                        data-testid={`region-health-fix-it-${r.region}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-white border border-red-300 rounded-full px-2 py-0.5 hover:bg-red-50 flex-shrink-0 transition-colors"
                      >
                        <Wrench className="w-3 h-3" /> Fix it
                      </button>
                    )}
                  </div>
                  {role === 'admin' && r.suggested_owner_candidates?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1 border-t border-red-200" data-testid={`region-health-suggestions-${r.region}`}>
                      {r.suggested_owner_candidates.map((c) => {
                        const key = `${c.username}-${r.region}`;
                        return (
                          <button
                            key={c.username}
                            type="button"
                            onClick={() => handleMakeOwner(c, r.region)}
                            disabled={assigningKey === key}
                            data-testid={`region-health-suggest-${r.region}-${c.username}`}
                            title={`${c.name} already manages ${c.regions.length} other region(s)`}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-white border border-emerald-300 rounded-full px-2 py-0.5 hover:bg-emerald-50 disabled:opacity-50 transition-colors"
                          >
                            <UserPlus className="w-3 h-3" /> {assigningKey === key ? 'Assigning…' : `Make ${c.name} owner`}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <UserCog className="w-3.5 h-3.5 flex-shrink-0" /> Owner: {r.owner_name || <span className="italic text-slate-400">None</span>}
              </div>
              <div className="flex items-center justify-between gap-1.5 text-xs text-slate-500" data-testid={`region-health-last-inbound-${r.region}`}>
                <span className="flex items-center gap-1.5">
                  <Inbox className="w-3.5 h-3.5 flex-shrink-0" /> Last inbound lead:{' '}
                  {r.last_inbound_lead_at ? formatManilaTime(r.last_inbound_lead_at, { withYear: true }) : <span className="italic text-slate-400">Never</span>}
                </span>
                <Sparkline data={r.trend} days={rangeDays} testId={`region-health-sparkline-${r.region}`} />
              </div>
              <div className="flex flex-wrap gap-3 pt-1 border-t border-slate-100">
                <Flag on={!!r.auto_assign} label="Auto-assign" />
                <Flag on={!!r.voucher_required} label="Voucher required" />
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export default RegionHealthPanel;
